import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import { SkeletonRows, ErrorNotice, InfoNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import { dayLabel, torontoToday } from '../../lib/events';
import { errorMessage, inputClass } from '../../lib/programs';
import type { Dashboard, KeyContact, PanelProps } from './types';
import PeopleList, { useInvitees } from './PeopleList';
import NotesPanel from './NotesPanel';
import { slotFor } from './registry';
import Placeholder from './Placeholder';
import { extrasFor } from './plugins';

function Contacts({ detail, mutable, refresh }: PanelProps) {
  const e = detail.event;
  const saved = e.settings.keyContacts ?? [];
  const [edit, setEdit] = useState(false);
  const [rows, setRows] = useState<KeyContact[]>(
    saved.length ? saved : [{ label: 'Venue', name: '', email: '', phone: '' }],
  );
  const save = useMutation(async () => api.patch(`/events/${e.id}`, { settings: { keyContacts: rows } }), {
    onSuccess: () => {
      setEdit(false);
      refresh();
    },
  });
  const set = (i: number, k: keyof KeyContact, v: string) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <Panel>
      <PanelHeader title="Key contacts">
        {mutable ? (
          <Button size="sm" variant="ghost" onClick={() => setEdit((v) => !v)}>
            {edit ? 'Cancel' : 'Edit'}
          </Button>
        ) : null}
      </PanelHeader>
      <PanelBody className="text-table">
        {e.contactEmail ? (
          <div className="border-b border-line py-[6px]">
            <span className="text-ink-2">Event inbox</span> · {e.contactEmail}
          </div>
        ) : null}
        {!edit ? (
          saved.length ? (
            saved.map((c, i) => (
              <div key={i} className="border-b border-line py-[6px]">
                <span className="text-ink-2">{c.label ?? 'Contact'}</span>
                {c.name ? ` · ${c.name}` : ''}
                {c.phone ? (
                  <>
                    {' '}
                    ·{' '}
                    <a className="underline" href={`tel:${c.phone}`}>
                      {c.phone}
                    </a>
                  </>
                ) : null}
                {c.email ? (
                  <>
                    {' '}
                    ·{' '}
                    <a className="underline" href={`mailto:${c.email}`}>
                      {c.email}
                    </a>
                  </>
                ) : null}
              </div>
            ))
          ) : (
            <p className="text-ink-2">
              No contacts yet{mutable ? ': add the venue, hotel, caterer and AV contacts.' : '.'}
            </p>
          )
        ) : (
          <>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[120px_1fr_1fr_130px_24px] gap-2 mt-2">
                <input
                  className={inputClass}
                  placeholder="Role"
                  value={r.label ?? ''}
                  onChange={(ev) => set(i, 'label', ev.target.value)}
                />
                <input
                  className={inputClass}
                  placeholder="Name"
                  value={r.name ?? ''}
                  onChange={(ev) => set(i, 'name', ev.target.value)}
                />
                <input
                  className={inputClass}
                  placeholder="Email"
                  value={r.email ?? ''}
                  onChange={(ev) => set(i, 'email', ev.target.value)}
                />
                <input
                  className={inputClass}
                  placeholder="Phone"
                  value={r.phone ?? ''}
                  onChange={(ev) => set(i, 'phone', ev.target.value)}
                />
                <button
                  className="text-ink-2"
                  title="Remove"
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </div>
            ))}
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRows((rs) => [...rs, { label: '', name: '', email: '', phone: '' }])}
              >
                Add a contact
              </Button>
              <Button size="sm" disabled={save.isLoading} onClick={() => save.mutate()}>
                Save
              </Button>
            </div>
            {save.error ? (
              <div className="mt-2">
                <ErrorNotice>{errorMessage(save.error, 'Could not save the contacts.')}</ErrorNotice>
              </div>
            ) : null}
          </>
        )}
      </PanelBody>
    </Panel>
  );
}

function WalkIns({ detail, day }: { detail: PanelProps['detail']; day: string }) {
  const list = useInvitees(detail.event.id);
  if (list.isLoading || !list.data) return null;
  const walkIns = list.data.invitees.filter((i) => i.source === 'walk_in' && i.checkedIn.includes(day));
  return (
    <div className="mt-2">
      {fmt(walkIns.length)} walk-in{walkIns.length === 1 ? '' : 's'} this day
    </div>
  );
}

/** One event day: agenda, who is expected, check-in, dietary list, contacts and the day's notes. */
export default function DuringDay(props: PanelProps) {
  const { detail, mutable } = props;
  const e = detail.event;
  const day = props.day ?? e.days[0].date;
  const q = useQuery<Dashboard>(
    ['event-dashboard', e.id],
    async () => (await api.get(`/events/${e.id}/dashboard`)).data.data,
    { refetchInterval: 30000 },
  );
  const [showExpected, setShowExpected] = useState(false);
  const Agenda = slotFor('dayAgenda');
  const Dietary = slotFor('dayDietary');
  const CheckinLinks = slotFor('checkinLinks');
  const agendaExtras = extrasFor('during.agenda');
  const dietaryExtras = extrasFor('during.dietary');
  const dayExtras = extrasFor('during.day');
  const today = q.data?.byDay.find((d) => d.date === day);
  const live = detail.live.find((d) => d.date === day);
  const provider = e.settings.checkinProvider;
  const isToday = day === torontoToday();
  const eventOver = (e.days[e.days.length - 1]?.date ?? '') < torontoToday();
  const platformCheckin = !provider;
  const dietaryIds = today?.inPerson.ids ?? [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className="lg:col-span-2 flex flex-col gap-5">
        {Agenda ? <Agenda {...props} day={day} /> : null}
        {agendaExtras.map((Extra, i) => (
          <Extra key={i} eventId={e.id} canEdit={mutable} day={day} />
        ))}
        {!Agenda && !agendaExtras.length ? (
          <Placeholder title={`Agenda and roll-out, ${dayLabel(day)}`} comingWith="speakers and sessions" />
        ) : null}
      </div>
      <Panel>
        <PanelHeader title={isToday ? 'Expected today' : 'Expected'} />
        <PanelBody className="text-table">
          {q.isLoading ? (
            <SkeletonRows rows={2} />
          ) : q.error || !today ? (
            <ErrorNotice>Could not load the numbers.</ErrorNotice>
          ) : (
            <>
              <div className="flex gap-6">
                <div>
                  <div className="font-semibold text-[30px] leading-none">{fmt(today.inPerson.value)}</div>
                  <div className="text-small text-ink-2">in person</div>
                </div>
                <div>
                  <div className="font-semibold text-[30px] leading-none">{fmt(today.virtual.value)}</div>
                  <div className="text-small text-ink-2">online</div>
                </div>
              </div>
              {q.data?.idsVisible && today.inPerson.ids ? (
                <>
                  <Button size="sm" variant="ghost" className="mt-3" onClick={() => setShowExpected((v) => !v)}>
                    {showExpected ? 'Hide the list' : 'Show the list'}
                  </Button>
                  {showExpected ? (
                    <div className="mt-2">
                      <PeopleList eventId={e.id} ids={today.inPerson.ids} />
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </PanelBody>
      </Panel>
      {platformCheckin && eventOver && !live?.checkedIn ? null : (
        <Panel>
          <PanelHeader title="Check-in" note={platformCheckin ? 'refreshes every 20 s' : undefined} />
          <PanelBody className="text-table">
            {!platformCheckin ? (
              <InfoNotice>
                Check-in runs in {provider || 'PheedLoop'}. Upload its check-in export in After · Attendance once the
                day is over.
              </InfoNotice>
            ) : live ? (
              <>
                <div className="flex gap-6">
                  <div>
                    <div className="font-semibold text-[30px] leading-none">{fmt(live.checkedIn)}</div>
                    <div className="text-small text-ink-2">checked in</div>
                  </div>
                  <div>
                    <div className="font-semibold text-[30px] leading-none">{fmt(live.expected)}</div>
                    <div className="text-small text-ink-2">expected</div>
                  </div>
                </div>
                <ul className="text-small text-ink-2 mt-3">
                  {live.byAudience.map((a) => (
                    <li key={a.key} className="flex justify-between gap-6">
                      <span>{a.name}</span>
                      <span>
                        {fmt(a.checkedIn)} / {fmt(a.expected)}
                      </span>
                    </li>
                  ))}
                </ul>
                <WalkIns detail={detail} day={day} />
              </>
            ) : (
              <p className="text-ink-2">No check-in numbers for this day.</p>
            )}
          </PanelBody>
        </Panel>
      )}
      {platformCheckin && mutable && CheckinLinks && (!eventOver || detail.links.some((l) => l.state === 'valid')) ? (
        <div className="lg:col-span-2">
          <CheckinLinks {...props} day={day} />
        </div>
      ) : null}
      {dietaryExtras.length ? (
        <div className="flex flex-col gap-5">
          {dietaryExtras.map((Extra, i) => (
            <Extra key={i} eventId={e.id} canEdit={mutable} day={day} />
          ))}
        </div>
      ) : Dietary ? (
        <Dietary {...props} day={day} />
      ) : (
        <Panel>
          <PanelHeader title={isToday ? 'Dietary needs today' : 'Dietary needs'} />
          <PanelBody className="text-table">
            {!q.data?.idsVisible ? (
              <InfoNotice>The dietary list is shown to admins; catering numbers are in Before · Documents.</InfoNotice>
            ) : (
              <DietaryList eventId={e.id} ids={dietaryIds} />
            )}
          </PanelBody>
        </Panel>
      )}
      {dayExtras.map((Extra, i) => (
        <Extra key={i} eventId={e.id} canEdit={mutable} day={day} />
      ))}
      <Contacts {...props} />
      <div className="lg:col-span-2">
        <NotesPanel {...props} day={day} />
      </div>
    </div>
  );
}

function DietaryList({ eventId, ids }: { eventId: string; ids: string[] }) {
  const list = useInvitees(eventId);
  if (list.isLoading || !list.data) return <SkeletonRows rows={3} />;
  const wanted = new Set(ids);
  const withDiet = list.data.invitees.filter((i) => wanted.has(i.id) && i.needs?.dietary).map((i) => i.id);
  return <PeopleList eventId={eventId} ids={withDiet} show="dietary" />;
}
